import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserCheck } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import { BondPayload, PersonalData, mensagemDeErro } from '../api';
import { useConverterCandidatura, useVinculoDaCandidatura } from '../hooks';
import { BondFields } from './BondFields';
import { cleanBondPayload, validateBondForm } from './format';
import { PersonalDataFields } from './PersonalDataFields';
import { PessoasModal } from './PessoasModal';

interface Props {
  applicationId: string;
  candidateName: string;
  /** Sugestões vindas da requisição da vaga. */
  defaults?: { companyId?: string; employmentType?: string };
}

/**
 * "Converter em colaborador" na candidatura aprovada (US-GP-001, fluxo
 * 13.1). Já convertida, mostra o atalho para o cadastro. Só aparece para
 * quem tem `pessoas.colaboradores`.
 */
export function ConverterCandidatura({ applicationId, candidateName, defaults }: Props) {
  const { can } = usePermissions();
  const podeVer = can(FEATURES.pessoasColaboradores, 'view');
  const podeCriar = can(FEATURES.pessoasColaboradores, 'create');
  const vinculo = useVinculoDaCandidatura(applicationId, podeVer);
  const [open, setOpen] = useState(false);

  if (!podeVer || vinculo.isLoading) return null;
  if (vinculo.data) {
    return (
      <Link to={`/pessoas/colaboradores/${vinculo.data.userId}`} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-green-700 hover:underline">
        <UserCheck className="h-3.5 w-3.5" /> Ver colaborador
      </Link>
    );
  }
  if (!podeCriar) return null;
  return (
    <>
      <Button size="sm" className="mt-2" icon={<UserCheck className="h-3.5 w-3.5" />} onClick={() => setOpen(true)}>
        Converter em colaborador
      </Button>
      {open && (
        <ConverterModal
          applicationId={applicationId}
          candidateName={candidateName}
          defaults={defaults}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function ConverterModal({ applicationId, candidateName, defaults, onClose }: Props & { onClose: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const converter = useConverterCandidatura(applicationId);
  const [rg, setRg] = useState('');
  const [pessoal, setPessoal] = useState<PersonalData>({ birthCountry: 'Brasil', nationality: 'Brasileira' });
  const [bond, setBond] = useState<Partial<BondPayload>>({
    companyId: defaults?.companyId,
    employmentType: defaults?.employmentType === 'pj' ? 'pj' : 'clt',
    contractKind: 'indeterminado',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const found = validateBondForm(bond);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    try {
      const criado = await converter.mutateAsync({
        ...pessoal,
        rg: rg.trim() || undefined,
        bond: cleanBondPayload(bond as BondPayload),
      });
      toast.success(
        criado.reusedPerson
          ? 'A pessoa já estava cadastrada: o novo vínculo foi criado no cadastro existente.'
          : 'Colaborador criado a partir do candidato.',
      );
      navigate(`/pessoas/colaboradores/${criado.id}`);
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível converter a candidatura.'));
    }
  }

  return (
    <PessoasModal
      open
      size="lg"
      title="Converter em colaborador"
      description={`${candidateName}: nome, CPF, e-mail e telefone vêm do candidato. Se o CPF já tiver cadastro, a pessoa é reaproveitada.`}
      submitLabel="Converter"
      isSubmitting={converter.isPending}
      submitError={error}
      onClose={onClose}
      onSubmit={submit}
    >
      <TextInput label="RG (obrigatório se a pessoa ainda não tem cadastro)" value={rg} onChange={(e) => setRg(e.target.value)} />
      <PersonalDataFields value={pessoal} onChange={(patch) => setPessoal((p) => ({ ...p, ...patch }))} />
      <h3 className="pt-2 text-sm font-semibold text-gray-900">Vínculo</h3>
      <BondFields value={bond} errors={errors} onChange={(patch) => setBond((b) => ({ ...b, ...patch }))} />
    </PessoasModal>
  );
}
